"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import type { Locale } from "@/lib/i18n";
import { BRAND_NAME } from "@/lib/brand";
import { getShellStrings } from "@/lib/shellStrings";

export function MobileMenu({ locale }: { locale: Locale }) {
  const pathname = usePathname();
  const s = getShellStrings(locale);
  const { nav } = s;
  const [open, setOpen] = useState(false);
  const canPortal = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  useEffect(() => {
    queueMicrotask(() => setOpen(false));
  }, [pathname, locale]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const menuLayer =
    open && canPortal ? (
      <div role="dialog" aria-modal="true" className="fixed inset-0 z-[2000] bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)}>
        <div
          className="absolute top-4 right-4 left-4 bottom-4 bg-white dark:bg-gray-950 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl overflow-auto p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <div className="font-serif text-lg font-bold">
              AMAZonA<span className="text-emerald-600 dark:text-emerald-400">.KG</span>
            </div>
            <button
              type="button"
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={s.ariaCloseMenu}
              onClick={() => setOpen(false)}
            >
              <span aria-hidden="true" className="text-lg leading-none">
                ✕
              </span>
            </button>
          </div>
          <div className="h-px bg-gray-200 dark:bg-gray-800 my-4" />
          <nav className="flex flex-col gap-2">
            <MenuLink href="/about" label={nav.about} />
            <MenuLink href="/mission" label={locale === "ru" ? "Миссия" : locale === "en" ? "Mission" : "Миссия"} />
            <MenuLink href="/approach" label={locale === "ru" ? "Подход" : locale === "en" ? "Approach" : "Ыкма"} />
            <MenuLink href="/areas" label={locale === "ru" ? "Направления" : locale === "en" ? "Areas" : "Багыттар"} />
            <MenuLink href="/projects" label={nav.projects} />
            <MenuLink href="/publications" label={nav.publications} />
            <MenuLink href="/news" label={nav.news} />
            <MenuLink href="/gallery" label={nav.gallery} />
            <MenuLink href="/partners" label={nav.partners} />
            <MenuLink href="/documents" label={nav.documents} />
            <MenuLink href="/contacts" label={nav.contacts} />
          </nav>
        </div>
      </div>
    ) : null;

  return (
    <>
      <button type="button" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" aria-label={s.ariaOpenMenu} onClick={() => setOpen(true)}>
        <span aria-hidden="true" className="text-lg leading-none">
          ☰
        </span>
      </button>
      {menuLayer ? createPortal(menuLayer, document.body) : null}
    </>
  );
}

function MenuLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors"
    >
      <span className="font-semibold">{label}</span>
      <span className="text-gray-400">→</span>
    </Link>
  );
}

